// ── 대시보드 스타일 ────────────────────────────────────────────────────────────
// safe-area env() 가 필요한 항목만 inline style로 유지하고,
// 나머지는 Tailwind 클래스 문자열로 관리합니다.
import { cn } from '../../lib/utils';

// ── inline style 유지 (env() / dynamic maxWidth) ───────────────────────────

/**
 * 페이지 최외곽 래퍼
 * safe-area-inset 처리를 위해 inline style 유지
 */
export function pageStyle(isTmMode) {
  return {
    background: 'var(--bg)',
    minHeight: '100dvh',
    width: '100%',
    maxWidth: '100%',
    overflowX: 'clip',
    padding: isTmMode
      ? 'max(8px, env(safe-area-inset-top)) max(4px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(4px, env(safe-area-inset-left))'
      : 'max(12px, env(safe-area-inset-top)) max(14px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(14px, env(safe-area-inset-left))',
  };
}

/**
 * 내부 콘텐츠 컨테이너
 * dynamic maxWidth 때문에 inline style 유지
 */
export function containerStyle(isWideMode) {
  return {
    width: '100%',
    maxWidth: isWideMode ? 1680 : 1080,
    margin: '0 auto',
    overflowX: 'clip',
  };
}

// ── Tailwind 클래스 문자열 ──────────────────────────────────────────────────

/** 로딩 페이지: 화면 중앙 정렬 */
export const loadingPageClass = 'min-h-dvh flex items-center justify-center text-[var(--text-sub)] text-sm';

/** 콘텐츠 카드 */
export const cardClass = 'bg-[var(--bg-card)] rounded-xl shadow-card border border-[var(--border)] p-5 w-full min-w-0';

/** 섹션 제목 */
export const sectionTitleClass = 'text-[15px] font-bold text-[var(--text)] tracking-tight mt-0 mb-3.5';

/** 단락 */
export const linePClass = 'my-1.5 text-sm text-[var(--text-sub)] overflow-wrap-anywhere break-words';

/** 탭 메뉴 행 — 가로 스크롤, 스크롤바 숨김 */
export const menuRowClass = [
  'flex gap-2 mb-4 items-center flex-nowrap',
  'overflow-x-auto',
  // Webkit/Firefox 스크롤바 숨김
  '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
].join(' ');

/**
 * 탭 버튼 클래스
 * @param {boolean} active      활성 탭 여부
 * @param {boolean} isDragging  드래그 중인 탭 여부
 * @param {boolean} isDragReady 0.8초 꾹 누름 완료 여부
 */
export function tabBtnClass(active, isDragging, isDragReady) {
  return cn(
    // 기본 레이아웃 — flex-shrink-0: 가로 스크롤 시 축소 방지
    'inline-flex items-center h-[34px] px-4 rounded-full text-[13px] font-semibold flex-shrink-0',
    'transition-all duration-150 whitespace-nowrap select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/50 focus-visible:ring-offset-1',
    // 활성/비활성
    active
      ? 'bg-pine-900 text-white dark:bg-accent-600 border border-transparent shadow-sm'
      : 'bg-transparent text-[var(--text-sub)] border border-[var(--border)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text)]',
    // 드래그 상태
    isDragging && 'opacity-30 scale-95',
    isDragReady ? 'cursor-grabbing' : 'cursor-grab',
  );
}

/** 탭 삽입 위치 줄 (드래그 중 커서 위치 표시) */
export const tabInsertLineClass = 'w-[3px] min-w-[3px] h-[30px] bg-accent-500 rounded-full flex-shrink-0 pointer-events-none';

/** 계정 메뉴 드롭다운 패널 */
export const accountMenuDropClass = cn(
  'absolute top-10 right-0 z-30 min-w-[160px]',
  'bg-[var(--bg-card)] border border-[var(--border)] rounded-xl shadow-popup',
  'p-1.5 flex flex-col gap-0.5',
);

/** 계정 메뉴 일반 항목 */
export const accountMenuItemClass = cn(
  'w-full text-left px-3 py-2 text-sm font-semibold rounded-lg',
  'text-[var(--text)] hover:bg-[var(--bg-subtle)]',
  'transition-colors cursor-pointer border-0 bg-transparent',
);

/** 계정 메뉴 로그아웃 항목 */
export const accountMenuLogoutClass = cn(
  'w-full text-left px-3 py-2 text-sm font-semibold rounded-lg',
  'text-danger hover:bg-danger-light',
  'transition-colors cursor-pointer border-0 bg-transparent',
);

/** 모달 내 상세 텍스트 */
export const detailLineClass = 'text-sm text-[var(--text-sub)] overflow-wrap-anywhere break-words';
